
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useToast } from '@/common-utils/hooks/use-toast';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import Icon from '../icon/Icon';
import ICONS from '@/common-utils/enums/icons';
import Entity from '../entity/Entity';

/**
 *
 * @param root0
 * @param root0.setFileUploaded
 * @param root0.fileExampleProperties
 * @param root0.uploadProperties
 * @param root0.manageFileData
 * @param root0.fileRemoved
 * @returns {JSX} FileUpload
 */
function FileUpload({ setFileUploaded, fileExampleProperties, uploadProperties, manageFileData, fileRemoved }) {

    // Toast
    const { toast } = useToast();

    // State
    const [fileExtentionsAccepted, setFileExtentionsAccepted] = useState([]);
    const [file, setFile] = useState([]);
    const [shouldAcceptFile, setShouldAcceptFile] = useState(false);
    const [entities, setEntities] = useState({
        fileExampleLabel: 'file_upload.default.file_example',
        btnUploadLabel: 'file_upload.default.button',
        cannotReadFile: 'file_upload.default.cannot_read_file',
        invalidFile: 'file_upload.default.invalid_file',
        invalidFileSize: 'file_upload.default.invalid_size_file'
    });


    // Hooks
    useEffect(() => {
        setFileExtentionsAccepted(uploadProperties.fileExtAccepted);

        if (fileExampleProperties) {
            if (fileExampleProperties.entity) {
                setEntities({ ...entities, fileExampleLabel: fileExampleProperties.entity });
            }
        }

        // uploadProperties is always present as one of its properties is required
        if (uploadProperties.btnUploadEntity) {
            setEntities({ ...entities, btnUploadLabel: uploadProperties.btnUploadEntity });
        }
        if (uploadProperties.cannotReadFileEntity) {
            setEntities({ ...entities, cannotReadFile: uploadProperties.cannotReadFileEntity });
        }
        if (uploadProperties.invalidFileEntity) {
            setEntities({ ...entities, invalidFile: uploadProperties.invalidFileEntity });
        }
        if (uploadProperties.invalidFileSizeEntity) {
            setEntities({ ...entities, invalidFileSize: uploadProperties.invalidFileSizeEntity });
        }
    }, [uploadProperties]);

    const onChangeFile = (event) => {
        const targetFile = event.target.files ? event.target.files[0] : null;

        // Errors handling on file
        // Checks if the file exists
        if (!targetFile || !targetFile.name || !targetFile.type) {
            toast({
                description: <Entity
                    entity={entities.cannotReadFile}
                    namespace={uploadProperties.cannotReadFileEntity && uploadProperties.namespace}
                />,
                variant: 'danger'
            });
            return null;
        }

        // Checks if the file has a good file extension (Required)
        if (!fileExtentionsAccepted.find(x => targetFile.name.includes(x))) {
            toast({
                description: <Entity
                    entity={entities.invalidFile}
                    namespace={uploadProperties.invalidFileEntity && uploadProperties.namespace}
                />,
                variant: 'danger'
            });
            return null;
        }

        // Checks if the file has the good size (Optionnal)
        if (uploadProperties.maxSize && targetFile.size > uploadProperties.maxSize) {
            toast({
                description: <Entity
                    entity={entities.invalidFileSize}
                    namespace={uploadProperties.invalidFileSizeEntity && uploadProperties.namespace}
                />,
                variant: 'danger'
            });
            return null;
        }

        // Start reading the file
        setShouldAcceptFile(true);
        setFile([]);
        const reader = new FileReader();
        reader.onload = (evt) => {
            setFile([targetFile]);
            setFileUploaded(manageFileData(evt.target.result));
        };
        reader.onerror = () => {
            setShouldAcceptFile(false);
            toast({
                description: <Entity
                    entity={entities.cannotReadFile}
                    namespace={uploadProperties.cannotReadFileEntity && uploadProperties.namespace}
                />,
                variant: 'danger'
            });
        };

        if (uploadProperties.typeOfUpload === 'data') {
            reader.readAsDataURL(targetFile);
        } else {
            reader.readAsText(targetFile, 'UTF-8');
        }

        // Reset event content
        event.target.files = null;
    };

    return (
        <div className='file-upload mt-4 text-center'>
            <Entity entity='file_upload.label' />
            <div className='flex gap-2 mt-2 mb-4 justify-center'>
                {
                    fileExampleProperties &&
                    <a href={fileExampleProperties.path} download={fileExampleProperties.name} className='flex max-w-fit'>
                        <label className={cn(
                            'bg-secondary text-secondary-foreground border border-secondary-foreground hover:bg-secondary-hover',
                            'example-file-download rounded-md px-4 py-2 cursor-pointer flex gap-2'
                        )}>
                            <Icon icon={ICONS.DOWNLOAD} />
                            <Entity entity={entities.fileExampleLabel} />
                        </label>
                    </a>
                }
                <label className={cn(
                    'bg-secondary text-secondary-foreground border border-secondary-foreground hover:bg-secondary-hover',
                    'custom-file-upload flex max-w-fit rounded-md px-4 py-2 cursor-pointer gap-2'
                )}>
                    <input id='fileContents' type='file' onChange={onChangeFile} accept={fileExtentionsAccepted.join(',')}
                        className='hidden' />
                    <Icon icon={ICONS.UPLOAD} />
                    <Entity entity={entities.btnUploadLabel} />
                </label>
            </div>
            {
                file.map((element, index) =>
                    <div key={`${index}-${element.name}`} className='file-preview' >
                        {shouldAcceptFile ?
                            <div className='flex gap-2 justify-center'>
                                <span>{element.name}</span>
                                <Icon
                                    icon={ICONS.DELETE}
                                    onClick={() => {
                                        setFile([]);
                                        setShouldAcceptFile(false);
                                        fileRemoved();
                                    }}
                                    className='cursor-pointer' />
                            </div>
                            :
                            <Entity entity={entities.invalidFile} />}
                    </div>
                )
            }
        </div >
    );
}

FileUpload.propTypes = {
    setFileUploaded: PropTypes.func.isRequired,
    fileExampleProperties: PropTypes.shape({
        path: PropTypes.string,
        fileName: PropTypes.string,
        entity: PropTypes.string,
        name: PropTypes.string
    }),
    uploadProperties: PropTypes.shape({
        namespace: PropTypes.string,
        fileExtAccepted: PropTypes.array.isRequired,
        typeOfUpload: PropTypes.string.isRequired,
        maxSize: PropTypes.number,
        btnUploadEntity: PropTypes.string,
        cannotReadFileEntity: PropTypes.string,
        invalidFileEntity: PropTypes.string,
        invalidFileSizeEntity: PropTypes.string
    }),
    manageFileData: PropTypes.func.isRequired,
    fileRemoved: PropTypes.func.isRequired
};

export default FileUpload;